import { observable, action, computed } from 'mobx';

import {
  CUSTOM_SOLUTIONS,
  DEVICE_MODULE_KEY,
  EVENT_MODULE_KEY,
  PARKING_MODULE_KEY,
  USER_MODULE_KEY
} from '@app/src/common/model/AppModel';
import { IUser } from '@app/src/components/user-management/model/User';
import UserManagementRepository from '../repository/UserManagementRepository';
import CompanySettingsRepository from '../repository/CompanySettingsRepository';
import AuthStore from './AuthStore';
import localeStore from '@app/src/common/store/LocaleStore';
import { getTimezoneByOffset } from '@app/src/common/components/timezone-select/constants/timezoneAliases';
import {
  deskManagementModeKey,
  deskTranslationPrefix
} from '@app/src/components/parking-management/constants/parkingModuleKeys';

const TIMEZONE_OFFSET_SESSION_KEY = 'timezone-offset';
const EVENT_TABS = [
  'VISITOR_COMPANY_TAB', // Company tab
  'EVENT_LOCATION', // Location tab
  'VIEW_VISITORS_ONSITE', // Visitors Onsite tab
  'WAIT_LIST_TAB', // Waitlist tab
  'RECEPTION_TAB' // Reception tab
];

class AppStore {
  @observable user: IUser | null;
  @observable configs = new Map<string, string | null>();
  @observable configurablePages: Map<string, boolean> = new Map();
  @observable currentTimezoneOffset;
  @observable formConfigsLoading = false;
  moduleSettings: Map<string, Map<string, string | null>> = new Map();
  activePlugins: string[];

  constructor() {
    this.currentTimezoneOffset = this.getInitialTimezoneOffset();
  }

  @computed
  get changedTimezone() {
    const systemTZOffset = new Date().getTimezoneOffset();
    return systemTZOffset !== this.currentTimezoneOffset;
  }

  @computed
  get activeTimezone() {
    return (
      getTimezoneByOffset(this.currentTimezoneOffset) ||
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  }

  @computed get fullName(): string {
    if (this.user && this.user.firstName && this.user.lastName) {
      return `${this.user.firstName} ${this.user.lastName}`;
    }
    return '';
  }

  @computed get avatarString(): string {
    if (this.user && this.user.firstName && this.user.lastName) {
      return (
        this.user.firstName[0].toUpperCase() +
        this.user.lastName[0].toUpperCase()
      );
    }
    return '';
  }

  @action
  async defineUser() {
    const promises: Promise<any>[] = [
      UserManagementRepository.getUser(AuthStore.userId),
      this.loadActivePlugins()
    ];

    if (AuthStore.userModule.includes(EVENT_MODULE_KEY)) {
      promises.push(this.loadConfigurablePages(EVENT_MODULE_KEY, EVENT_TABS));
    }

    if (AuthStore.userModule.includes(DEVICE_MODULE_KEY)) {
      promises.push(
        this.loadConfigurablePage(DEVICE_MODULE_KEY, 'FULL_POP_LOGGING')
      );
    }

    if (AuthStore.userModule.includes(USER_MODULE_KEY)) {
      promises.push(
        this.loadConfigurablePage(USER_MODULE_KEY, 'ENABLE_TENANT_MANAGEMENT')
      );
    }

    if (AuthStore.userModule.includes(PARKING_MODULE_KEY)) {
      promises.push(
        this.loadConfigurablePage(PARKING_MODULE_KEY, deskManagementModeKey)
      );
    }

    const [user] = await Promise.all(promises);

    this.user = user;
    await localeStore.defineLanguage(
      user?.language,
      this.configurablePages.get(deskManagementModeKey)
        ? [deskTranslationPrefix]
        : []
    );
    return user;
  }

  @action logOut() {
    this.user = null;
    this.currentTimezoneOffset = new Date().getTimezoneOffset();
  }

  @action
  loadConfigurablePage(module: string, key: string) {
    return CompanySettingsRepository.getSetting(module, key).then(({ value }) =>
      this.configurablePages.set(key, !!(value && value !== 'false'))
    );
  }

  @action
  async loadConfigurablePages(module: string, keys: string[]) {
    return CompanySettingsRepository.getSettings(module, keys).then(response =>
      response.forEach(item =>
        this.configurablePages.set(item.key, !!item.value)
      )
    );
  }

  @action
  loadActivePlugins() {
    return UserManagementRepository.getActivePlugins().then(
      plugins => (this.activePlugins = plugins)
    );
  }

  @action
  updateTimezoneOffset(offset: number) {
    this.currentTimezoneOffset = offset;
    sessionStorage.setItem(TIMEZONE_OFFSET_SESSION_KEY, `${offset}`);
    location.reload();
  }

  getInitialTimezoneOffset = () => {
    const sessionOffset = sessionStorage.getItem(TIMEZONE_OFFSET_SESSION_KEY);
    return sessionOffset
      ? Number(sessionOffset)
      : new Date().getTimezoneOffset();
  };

  loadConfig(module: string, keys: string[]) {
    if (AuthStore.user) {
      this.formConfigsLoading = module === EVENT_MODULE_KEY;
      CompanySettingsRepository.getSettings(module, keys)
        .then(result =>
          result.forEach(item => this.configs.set(item.key, item.value))
        )
        .finally(() => (this.formConfigsLoading = false));
    }
  }

  getModuleSetting(module: string, key: string) {
    const settings = this.moduleSettings.get(module) || new Map();
    if (settings.has(key)) {
      return Promise.resolve(settings.get(key));
    } else {
      return CompanySettingsRepository.getSetting(module, key).then(
        response => {
          settings.set(key, response.value || false);
          this.moduleSettings.set(module, settings);
          return response.value || false;
        }
      );
    }
  }

  loadCustomSolutionSetting() {
    return this.getModuleSetting(USER_MODULE_KEY, CUSTOM_SOLUTIONS);
  }
}

export default new AppStore();
